import { useQueryClient } from '@tanstack/react-query';
import { loginSchema, registerSchema, type AuthResponse } from '@rw/shared';
import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { Button } from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { useAuth } from '../../stores/auth';
import { useNetwork } from '../../stores/network';
import { SocialButtons } from './SocialButtons';
import { LANGS, useLang, useT } from '../../i18n';
import { LegalLinks } from '../legal/LegalPages';

export function AuthPage({ mode }: { mode: 'login' | 'register' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const t = useT();
  const setSession = useAuth((s) => s.setSession);
  const online = useNetwork((s) => s.online);
  const [form, setForm] = useState({ email: '', password: '', displayName: '' });
  const [error, setError] = useState<string | null>(() => {
    const r = new URLSearchParams(location.search).get('strava');
    return r === 'denied' ? t('Strava access was not granted.') : r ? t('Strava sign-in failed, please try again.') : null;
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const isRegister = mode === 'register';

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const schema = isRegister ? registerSchema : loginSchema;
    const parsed = schema.safeParse(isRegister ? form : { email: form.email, password: form.password });
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      for (const i of parsed.error.issues) fe[String(i.path[0])] ??= t(i.message);
      setFieldErrors(fe);
      return;
    }
    setFieldErrors({});
    setLoading(true);
    try {
      const res = await api<AuthResponse>(isRegister ? '/auth/register' : '/auth/login', { body: parsed.data });
      qc.clear();
      setSession(res.token, res.user);
      const to = (location.state as { from?: string } | null)?.from ?? '/';
      navigate(to, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('Something went wrong'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="bg-grid-glow flex min-h-full flex-col px-6 pt-[calc(var(--safe-top)+48px)] pb-[calc(var(--safe-bottom)+24px)]">
      <div className="mx-auto w-full max-w-sm flex-1">
        <div className="mb-8 flex flex-col items-center text-center">
          <img
            src="/brand/logo.png"
            alt="Runner Wars"
            width={960}
            height={646}
            className="h-auto w-full max-w-sm drop-shadow-[0_0_28px_rgba(255,197,61,0.25)]"
            data-testid="brand-logo"
          />
          <h1 className="sr-only">Runner Wars</h1>
          <p className="mt-4 font-display text-lg font-semibold uppercase tracking-[0.3em] text-ink-400">{t('Run · Conquer · Compete')}</p>
        </div>

        <form onSubmit={submit} className="space-y-4" noValidate>
          {isRegister ? (
            <Field
              label={t('Runner name')}
              name="displayName"
              autoComplete="nickname"
              value={form.displayName}
              error={fieldErrors.displayName}
              onChange={(v) => setForm({ ...form, displayName: v })}
            />
          ) : null}
          <Field
            label="Email"
            name="email"
            type="email"
            autoComplete="email"
            inputMode="email"
            value={form.email}
            error={fieldErrors.email}
            onChange={(v) => setForm({ ...form, email: v })}
          />
          <Field
            label={t('Password')}
            name="password"
            type="password"
            autoComplete={isRegister ? 'new-password' : 'current-password'}
            value={form.password}
            error={fieldErrors.password}
            onChange={(v) => setForm({ ...form, password: v })}
          />
          {error ? (
            <p className="rounded-xl border border-enemy/40 bg-enemy/10 px-3 py-2 text-sm text-enemy" role="alert" data-testid="auth-error">
              {error}
            </p>
          ) : null}
          {!online ? <p className="text-sm text-warn">{t('You’re offline. Connect to sign in.')}</p> : null}
          <Button type="submit" size="lg" className="w-full" loading={loading} disabled={!online} data-testid="auth-submit">
            {t(isRegister ? 'Create runner' : 'Sign in')}
          </Button>
        </form>

        <SocialButtons
          mode={mode}
          onSignedIn={() => navigate((location.state as { from?: string } | null)?.from ?? '/', { replace: true })}
          onError={setError}
        />

        <p className="mt-6 text-center text-sm text-ink-400">
          {t(isRegister ? 'Already running with us?' : 'New runner?')}{' '}
          <Link to={isRegister ? '/login' : '/register'} className="font-semibold text-volt" replace>
            {t(isRegister ? 'Sign in' : 'Create an account')}
          </Link>
        </p>
        <p className="mt-5 text-center text-sm">
          <Link to="/guide" className="font-semibold text-ink-200 underline underline-offset-4" data-testid="guide-link">
            📖 {t('How to install and play')}
          </Link>
        </p>
        <p className="mt-4 flex justify-center gap-4 text-xs">
          <LangSwitch />
        </p>
        <LegalLinks className="mt-4" />
      </div>
    </main>
  );
}

/** EN / VI before signing in (the same setting as Profile → Language). */
function LangSwitch() {
  const lang = useLang((s) => s.lang);
  const setLang = useLang((s) => s.setLang);
  return (
    <>
      {LANGS.map((l) => (
        <button key={l.code} onClick={() => setLang(l.code)} className={lang === l.code ? 'font-bold text-volt' : 'text-ink-400'} data-testid={`auth-lang-${l.code}`}>
          {l.label}
        </button>
      ))}
    </>
  );
}

function Field({
  label,
  error,
  onChange,
  ...props
}: { label: string; error?: string; onChange: (v: string) => void } & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const id = `f-${props.name}`;
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-ink-400">
        {label}
      </label>
      <input
        id={id}
        {...props}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={!!error}
        className={`h-13 w-full rounded-xl border bg-ink-850 px-4 text-base text-ink-100 outline-none transition placeholder:text-ink-500 focus:border-volt ${
          error ? 'border-enemy' : 'border-ink-700'
        }`}
      />
      {error ? <p className="mt-1 text-xs text-enemy">{error}</p> : null}
    </div>
  );
}
